import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMXN, formatDate, CATEGORIAS, STATUSES, statusColor } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function QuotationList() {
  const [items, setItems] = useState([]);
  const [categoria, setCategoria] = useState("Todas");
  const [status, setStatus] = useState("Todos");

  const load = async () => {
    const params = {};
    if (categoria !== "Todas") params.categoria = categoria;
    if (status !== "Todos") params.status = status;
    const { data } = await api.get("/cotizaciones", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [categoria, status]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    await api.delete(`/cotizaciones/${id}`);
    toast.success("Cotización eliminada");
    load();
  };

  return (
    <div className="space-y-8" data-testid="quotation-list-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="uppercase-label">Documentos</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Cotizaciones</h1>
          <p className="text-sm text-muted-foreground mt-2">Gestiona presupuestos de material y mano de obra.</p>
        </div>
        <Link to="/cotizaciones/nueva" data-testid="list-cta-new">
          <Button className="bg-[#8B5A33] hover:bg-[#6D4525] text-white">
            <Plus className="w-4 h-4 mr-2" /> Nueva cotización
          </Button>
        </Link>
      </header>

      <div className="flex flex-wrap gap-3">
        <Select value={categoria} onValueChange={setCategoria}>
          <SelectTrigger className="w-52 bg-white" data-testid="filter-categoria"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Todas">Todas las categorías</SelectItem>
            {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-52 bg-white" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Todos">Todos los estados</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-white rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F2EC]/70 text-left">
            <tr>
              <th className="px-4 py-3 uppercase-label">Folio</th>
              <th className="px-4 py-3 uppercase-label">Cliente</th>
              <th className="px-4 py-3 uppercase-label">Proyecto</th>
              <th className="px-4 py-3 uppercase-label">Categoría</th>
              <th className="px-4 py-3 uppercase-label">Estado</th>
              <th className="px-4 py-3 uppercase-label text-right">Subtotal</th>
              <th className="px-4 py-3 uppercase-label text-right">Total</th>
              <th className="px-4 py-3 uppercase-label">Fecha</th>
              <th className="px-4 py-3 uppercase-label w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-6 py-10 text-center text-muted-foreground">Sin cotizaciones.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`quote-row-${c.folio}`}>
                <td className="px-4 py-3">
                  <Link to={`/cotizaciones/${c.id}`} className="text-[#8B5A33] hover:underline tabular">{c.folio}</Link>
                </td>
                <td className="px-4 py-3">{c.cliente_nombre}</td>
                <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{c.proyecto}</td>
                <td className="px-4 py-3">{c.categoria}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded border ${statusColor(c.status)}`}>{c.status}</span>
                </td>
                <td className="px-4 py-3 text-right tabular">{formatMXN(c.subtotal)}</td>
                <td className="px-4 py-3 text-right tabular font-medium">{formatMXN(c.total)}</td>
                <td className="px-4 py-3 text-muted-foreground">{formatDate(c.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <Link to={`/cotizaciones/${c.id}`}>
                      <Button size="icon" variant="ghost" data-testid={`quote-open-${c.folio}`}><ExternalLink className="w-4 h-4" /></Button>
                    </Link>
                    <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`quote-delete-${c.folio}`}>
                      <Trash2 className="w-4 h-4 text-[#BC4749]" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
